/**
 * A titled panel. Every page that is not the battlefield is built from these,
 * including the "Coming in the next milestone" placeholders, so the shell looks
 * finished while the other milestones are still being written.
 */

import type { ReactNode } from 'react';
import styles from './Panel.module.css';

export interface PanelProps {
  title?: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Removes the inner padding, for panels that hold their own scroller. */
  bare?: boolean;
}

export function Panel({ title, subtitle, actions, children, className, bare }: PanelProps) {
  return (
    <section className={[styles.panel, className].filter(Boolean).join(' ')}>
      {title ? (
        <header className={styles.header}>
          <div className={styles.heading}>
            <h2 className={styles.title}>{title}</h2>
            {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
          </div>
          {actions ? <div className={styles.actions}>{actions}</div> : null}
        </header>
      ) : null}
      <div className={bare ? styles.bodyBare : styles.body}>{children}</div>
    </section>
  );
}

/**
 * The stand-in every page that belongs to a later milestone renders. It states
 * plainly what is coming rather than pretending to be broken.
 */
export function ComingSoonPanel({ title, note }: { title: string; note?: string }) {
  return (
    <Panel title={title}>
      <div className={styles.comingSoon}>
        <p className={styles.comingSoonLine}>Coming in the next milestone</p>
        {note ? <p className={styles.comingSoonNote}>{note}</p> : null}
      </div>
    </Panel>
  );
}
